import { ExternalLinkIcon } from 'lucide-react'
import { useEffect, useId, useReducer, useRef } from 'react'
import type { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  CONTACT_LIMITS,
  contactSchema,
  MINUTES_TO_APPLY,
} from '@/lib/audition/steps'
import { NewTabLink } from './new-tab-link'

export interface ContactValues {
  email: string
  firstName: string
  lastName: string
  phone?: string
  linkedInUrl?: string
  linkedInOptedOut: boolean
  acceptedTerms: boolean
  marketingConsent: boolean
}

/** What a valid Contact step hands on: the schema's output, normalized. */
export type ContactSubmission = z.output<typeof contactSchema>

/**
 * LinkedIn's own self-redirect. Signed in, it lands on the visitor's own
 * profile, so nobody has to remember their handle.
 */
const LINKEDIN_SELF_URL = 'https://www.linkedin.com/in/me/'

type ContactKey = keyof z.input<typeof contactSchema>

const IDENTITY_MESSAGE = 'Please fill in your name and a valid email address.'
const IDENTITY_FIELDS: readonly PropertyKey[] = [
  'firstName',
  'lastName',
  'email',
  'phone',
] satisfies readonly ContactKey[]
/** The keys whose refusal is the schema's own sentence. */
const OWN_MESSAGE_FIELDS = [
  'linkedInUrl',
  'acceptedTerms',
] as const satisfies readonly ContactKey[]
/** For a refused key neither list names, so Next never fails in silence. */
const FALLBACK_MESSAGE = 'Something on this step still needs an answer.'

/**
 * What Next refused, by key, with the sentence said for each at the time.
 * The alert is a live region, read out over whatever the applicant is
 * typing, so between presses a sentence only ever comes down — or is
 * reworded once they leave the box, never while they type in it.
 */
type Refusal = ReadonlyMap<PropertyKey, string>

function refusalOf(issues: readonly z.ZodIssue[]): Refusal {
  return new Map(issues.map((issue) => [issue.path[0] ?? '', issue.message]))
}

/** The refusal a press of Next would give these values right now. */
function refusalNow(values: ContactValues): Refusal {
  const parsed = contactSchema.safeParse(values)
  return parsed.success ? new Map() : refusalOf(parsed.error.issues)
}

/**
 * The refusal with everything since answered taken out. A half-typed link
 * still owes its sentence, in the words it was refused with; a key satisfied
 * once is not asked again until Next is pressed again.
 */
function stillOwed(refusal: Refusal, values: ContactValues): Refusal {
  const now = refusalNow(values)
  return new Map([...refusal].filter(([key]) => now.has(key)))
}

/**
 * The refusal reworded to what each box it names now says. For a pause —
 * leaving the box — not a keystroke: the shortcut pasted from the label link
 * still fails the "missing" key, and this is when it gets told what to do.
 */
function reworded(refusal: Refusal, values: ContactValues): Refusal {
  return new Map([...refusalNow(values)].filter(([key]) => refusal.has(key)))
}

/**
 * The form's own values: every box filled in, '' where the defaults had
 * nothing, so each input is provably controlled. Only the defaults coming in
 * may leave a box out.
 */
type FormValues = Required<ContactValues>
type TextKey = {
  [K in keyof FormValues]: FormValues[K] extends string ? K : never
}[keyof FormValues]
type BooleanKey = {
  [K in keyof FormValues]: FormValues[K] extends boolean ? K : never
}[keyof FormValues]
/**
 * The boxes a plain tick may write. The opt-out is left out: it is a
 * two-field write, the link cleared with it, and the `optOut` action owns
 * that rule.
 */
const OPT_OUT_KEY = 'linkedInOptedOut' satisfies BooleanKey
type TickKey = Exclude<BooleanKey, typeof OPT_OUT_KEY>

// Held by the compiler, in the style of `_everyAnswerShapeHasAnArm`: every
// field the schema asks for has a box here, every box can be edited, and
// every typed box has a cap.
const _everyContactFieldHasABox: Exclude<
  ContactKey,
  keyof FormValues
> extends never
  ? true
  : 'FormValues is missing a box for a contactSchema field' = true
const _everyBoxIsTypedOrTicked: Exclude<
  keyof FormValues,
  TextKey | BooleanKey
> extends never
  ? true
  : 'a FormValues box is neither typed nor ticked' = true
const _everyTypedBoxIsCapped: Exclude<
  TextKey,
  keyof typeof CONTACT_LIMITS
> extends never
  ? true
  : 'a typed box has no CONTACT_LIMITS cap' = true

interface ContactForm {
  values: FormValues
  refusal: Refusal
  /**
   * Refused presses of Next. Counted so a repeat press with the same words
   * is still a new state: focus moves to the alert and it is announced
   * again, instead of Next appearing to do nothing.
   */
  refusals: number
}

/**
 * Key and value travel together, so a box can only be given a value of its
 * own kind: a computed `{ [key]: value }` patch would type as an index
 * signature and check nothing.
 */
type ContactAction =
  | { type: 'type'; key: TextKey; value: string }
  | { type: 'tick'; key: TickKey; value: boolean }
  | { type: 'optOut'; optedOut: boolean }
  | { type: 'settle' }
  | { type: 'refuse'; issues: readonly z.ZodIssue[] }

/**
 * Values and refusal move together — an edit prunes the refusal against the
 * values it produced — so they are one state, and no write can leave the
 * other behind.
 */
function contactReducer(form: ContactForm, action: ContactAction): ContactForm {
  switch (action.type) {
    case 'type':
      return edited(form, { ...form.values, [action.key]: action.value })
    case 'tick':
      return edited(form, { ...form.values, [action.key]: action.value })
    case 'optOut':
      // One write: ticking clears the link, so no orphan URL survives
      // beside "I don't have one".
      return edited(form, {
        ...form.values,
        linkedInOptedOut: action.optedOut,
        linkedInUrl: action.optedOut ? '' : form.values.linkedInUrl,
      })
    case 'settle':
      // Nothing refused, nothing to reword: the same state back, so a blur
      // on the first pass through the form re-renders nothing.
      if (form.refusal.size === 0) return form
      return { ...form, refusal: reworded(form.refusal, form.values) }
    case 'refuse':
      return {
        ...form,
        refusal: refusalOf(action.issues),
        refusals: form.refusals + 1,
      }
  }
}

function edited(form: ContactForm, values: FormValues): ContactForm {
  return { ...form, values, refusal: stillOwed(form.refusal, values) }
}

function initialForm(defaults: Partial<ContactValues>): ContactForm {
  return {
    values: {
      email: defaults.email ?? '',
      firstName: defaults.firstName ?? '',
      lastName: defaults.lastName ?? '',
      phone: defaults.phone ?? '',
      linkedInUrl: defaults.linkedInUrl ?? '',
      linkedInOptedOut: defaults.linkedInOptedOut ?? false,
      acceptedTerms: defaults.acceptedTerms ?? false,
      marketingConsent: defaults.marketingConsent ?? false,
    },
    refusal: new Map(),
    refusals: 0,
  }
}

/**
 * One sentence per thing still missing, in the order the form asks for them.
 * The LinkedIn and terms sentences are the schema's own messages, so the
 * server's rule and what the applicant is told cannot drift apart.
 */
function refusalMessages(refusal: Refusal): string[] {
  const messages: string[] = []
  if ([...refusal.keys()].some((key) => IDENTITY_FIELDS.includes(key)))
    messages.push(IDENTITY_MESSAGE)
  for (const key of OWN_MESSAGE_FIELDS) {
    const message = refusal.get(key)
    if (message) messages.push(message)
  }
  if (messages.length === 0 && refusal.size > 0) messages.push(FALLBACK_MESSAGE)
  return messages
}

export function ContactStep({
  defaults,
  onSubmit,
  isSubmitting,
}: {
  defaults: Partial<ContactValues>
  onSubmit: (values: ContactSubmission) => void
  isSubmitting: boolean
}) {
  // The refusal is recorded when Next is pressed and only ever shrinks
  // between presses: each sentence comes down the moment its answer is
  // given, in the words it was said with, and nothing is added until Next
  // is pressed again.
  const [{ values, refusal, refusals }, dispatch] = useReducer(
    contactReducer,
    defaults,
    initialForm,
  )
  const errors = refusalMessages(refusal)
  const optOutNoteId = useId()
  const alertId = useId()
  const alert = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    // Focus follows the refusal, as QuestionStep's does: the message can sit
    // below the button, and a screen-reader user left where they were is
    // told nothing about why Next did nothing.
    if (refusals > 0) alert.current?.focus()
  }, [refusals])

  const set = (key: TextKey) => (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatch({ type: 'type', key, value: e.target.value })
  const tick = (key: TickKey) => (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatch({ type: 'tick', key, value: e.target.checked })
  const settle = () => dispatch({ type: 'settle' })
  /** What a box the refusal names says of itself: invalid, and why. */
  const refused = (key: ContactKey, helpId?: string) => ({
    'aria-invalid': refusal.has(key) || undefined,
    'aria-describedby':
      [helpId, refusal.has(key) ? alertId : undefined]
        .filter(Boolean)
        .join(' ') || undefined,
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    // Same schema the audition.start procedure validates with — it owns the
    // trim/lowercase normalization, the empty-phone → undefined convention
    // and the LinkedIn and consent rules, so a server-side rule change
    // surfaces here as inline feedback instead of a generic mutation error.
    const parsed = contactSchema.safeParse(values)
    if (!parsed.success) {
      dispatch({ type: 'refuse', issues: parsed.error.issues })
      return
    }
    onSubmit(parsed.data)
  }

  return (
    // noValidate: the schema is the one validator. Left to the browser, a
    // malformed email would be caught before submit fired, in the browser's
    // bubble, naming the email alone and holding every other sentence back.
    <form onSubmit={submit} noValidate className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">
          Apply to the coaching program
        </h1>
        <p className="text-muted-foreground">
          This takes about {MINUTES_TO_APPLY} minutes. Your progress is saved as
          you go.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="firstName">First name</Label>
          <Input
            id="firstName"
            value={values.firstName}
            onChange={set('firstName')}
            onBlur={settle}
            maxLength={CONTACT_LIMITS.firstName}
            autoComplete="given-name"
            {...refused('firstName')}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lastName">Last name</Label>
          <Input
            id="lastName"
            value={values.lastName}
            onChange={set('lastName')}
            onBlur={settle}
            maxLength={CONTACT_LIMITS.lastName}
            autoComplete="family-name"
            {...refused('lastName')}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={values.email}
          onChange={set('email')}
          onBlur={settle}
          maxLength={CONTACT_LIMITS.email}
          autoComplete="email"
          {...refused('email')}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="phone">Phone (optional)</Label>
        <Input
          id="phone"
          type="tel"
          value={values.phone}
          onChange={set('phone')}
          onBlur={settle}
          maxLength={CONTACT_LIMITS.phone}
          autoComplete="tel"
          {...refused('phone')}
        />
      </div>
      <div className="space-y-1.5">
        {/* The label row IS the link (LG-898 canvas): one target, not a
            second "open my profile" line that pushes the input away from
            its own label. The box takes its name from aria-label, so it is
            announced as "LinkedIn profile" rather than inheriting the link's
            "(opens in a new tab)". */}
        <NewTabLink
          href={LINKEDIN_SELF_URL}
          className="inline-flex items-center gap-1.5 text-sm font-medium leading-none underline underline-offset-4"
        >
          LinkedIn profile
          <ExternalLinkIcon
            aria-hidden="true"
            className="text-muted-foreground size-3.5"
          />
        </NewTabLink>
        <p id="linkedInUrl-help" className="text-muted-foreground text-sm">
          Ten seconds to grab, and it tells us more about your career than
          anything you could type here.
        </p>
        <Input
          id="linkedInUrl"
          aria-label="LinkedIn profile"
          inputMode="url"
          autoComplete="url"
          placeholder="linkedin.com/in/yourname"
          value={values.linkedInUrl}
          disabled={values.linkedInOptedOut}
          onChange={set('linkedInUrl')}
          onBlur={settle}
          maxLength={CONTACT_LIMITS.linkedInUrl}
          {...refused('linkedInUrl', 'linkedInUrl-help')}
        />
        <label className="flex items-start gap-2 pt-1 text-sm">
          <input
            type="checkbox"
            checked={values.linkedInOptedOut}
            aria-describedby={
              values.linkedInOptedOut ? optOutNoteId : undefined
            }
            onChange={(e) =>
              dispatch({ type: 'optOut', optedOut: e.target.checked })
            }
            className="accent-primary mt-0.5 size-4 shrink-0"
          />
          {"I don't have a LinkedIn profile"}
        </label>
        {values.linkedInOptedOut && (
          // Red, and worded as the consequence it is. A grey aside read as
          // reassurance, which is the opposite of what it means.
          <p id={optOutNoteId} className="text-destructive text-sm">
            This counts against you. Nearly everyone hiring expects a LinkedIn,
            and without one we have far less to go on, which makes it harder for
            us to help you. A LinkedIn isn’t required to <em>apply</em> to the
            program, but it is required to join it. Ticking this box reads as a
            red flag on your application, not a neutral blank.
          </p>
        )}
      </div>
      <div className="space-y-3 border-t pt-4">
        {/* Two boxes on purpose. Terms are required to apply; marketing is
            not, and a consent that is required in order to apply is not
            consent at all. */}
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={values.acceptedTerms}
            onChange={tick('acceptedTerms')}
            // Drawn as refused, not only announced: the text boxes beside
            // it turn red through the same aria-invalid. A ring, as they
            // use, so the browser's focus outline still shows on top of it.
            className="accent-primary mt-0.5 size-4 shrink-0 aria-invalid:ring-2 aria-invalid:ring-destructive/60"
            {...refused('acceptedTerms')}
          />
          <span>
            I agree to the{' '}
            <NewTabLink href="/terms" className="underline underline-offset-4">
              terms and conditions
            </NewTabLink>{' '}
            and the{' '}
            <NewTabLink
              href="/privacy"
              className="underline underline-offset-4"
            >
              privacy policy
            </NewTabLink>
            .
          </span>
        </label>
        <label className="text-muted-foreground flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={values.marketingConsent}
            onChange={tick('marketingConsent')}
            className="accent-primary mt-0.5 size-4 shrink-0"
          />
          <span>
            Email and text me about the program. Optional, and you can
            unsubscribe at any time.
          </span>
        </label>
      </div>
      {errors.length > 0 && (
        <p
          ref={alert}
          id={alertId}
          role="alert"
          // Focusable only by being sent here; it never joins the tab order.
          tabIndex={-1}
          className="text-destructive text-sm outline-none"
        >
          {errors.join(' ')}
        </p>
      )}
      <Button type="submit" disabled={isSubmitting}>
        Next
      </Button>
    </form>
  )
}
